import * as React from 'react';
import * as _ from 'lodash';
import * as moment from 'moment';
import { Dispatch, connect } from "react-redux";
import { Button, FormControl, FormGroup, ToggleButton, ToggleButtonGroup } from 'react-bootstrap';
import MediaQuery from 'react-responsive';
import Loader from 'react-spinners/ClockLoader';
import { IWatrAuditReportResponse } from '@nkr/store/watr/actions';
import { IWatrReimbursement } from '@nkr/models/index';
import { IApplicationState, AppActionCreators, IAppState, WatrActionCreators } from '@nkr/store/index';
import { ColumnApi, GridApi, GridReadyEvent, FirstDataRenderedEvent, GridSizeChangedEvent } from 'ag-grid-community';
import { OrganType, ReimbursementType } from '@nkr/enums/index';
import YearEndReportGrid, { AuditDisplayRow, AuditExceptionFlags, DuplicatePeer, evaluateHighDollarPayments, isCreditReturnPayment } from './year-end-report-grid';

type DatePreset = 'last90' | 'thisMonth' | 'priorYear';
type PaymentDirectionFilter = 'both' | 'debit' | 'credit';
type ReimbursementFamilyFilter = 'both' | 'wages' | 'travel';

type DiscrepancyFilter =
    | 'all'
    | 'noConfirmation'
    | 'possibleDuplicate'
    | 'highDollar'
    | 'complications'
    | 'paidBeforeDonation'
    | 'incompleteTaxId'
    | 'manyPayments'
    | 'typeMismatch';

const HIGH_DOLLAR_THRESHOLD = 5000;
const MANY_PAYMENTS_THRESHOLD = 3;

const WAGES_FAMILY = [
    ReimbursementType.Wages,
    ReimbursementType.WagesForComplication,
    ReimbursementType.CreditReturnLostWageReimbursement,
];

const TRAVEL_FAMILY = [
    ReimbursementType.TravelAndLodging,
    ReimbursementType.TravelAndLodgingForComplication,
];

const isWagesFamilyType = (type: number): boolean => WAGES_FAMILY.indexOf(type) >= 0;
const isTravelFamilyType = (type: number): boolean => TRAVEL_FAMILY.indexOf(type) >= 0;
const isYearEndEligibleType = (type: number): boolean => isWagesFamilyType(type) || isTravelFamilyType(type);
const yearEndFamilyKey = (type: number): 'wages' | 'travel' | 'other' =>
    isWagesFamilyType(type) ? 'wages' : isTravelFamilyType(type) ? 'travel' : 'other';

const COMPLICATION_TYPES = [
    ReimbursementType.WagesForComplication,
    ReimbursementType.TravelAndLodgingForComplication,
];

const ADVANCED_REIMBURSEMENT_TYPES = [
    ReimbursementType.AdvancedReimbursement,
    ReimbursementType.AdvancedReimbursementPreOp,
    ReimbursementType.AdvancedReimbursementDonation,
];

const isAdvancedReimbursementType = (type: number): boolean =>
    ADVANCED_REIMBURSEMENT_TYPES.indexOf(type) >= 0;

const toDuplicatePeer = (row: IWatrReimbursement): DuplicatePeer => ({
    id: row.id,
    requestId: row.requestId,
    date: row.date,
    amount: Number(row.amount || 0),
    confirmationCode: row.confirmationCode || '',
    donorAlias: row.donorAlias || '',
    fullName: row.fullName || `${row.firstName || ''} ${row.lastName || ''}`.trim(),
    email: row.email || '',
    phone: row.phone || '',
    reimbursementType: row.reimbursementType,
    centerAlias: row.centerAlias || '',
    weeksRequested: row.weeksRequested || '',
});

const normalizeEmail = (value?: string | null): string => (value || '').trim().toLowerCase();

const normalizePhone = (value?: string | null): string => {
    const digits = (value || '').replace(/\D/g, '');
    if (!digits) return '';
    return digits.length > 10 ? digits.slice(-10) : digits;
};

const normalizeName = (row: IWatrReimbursement): string => {
    const full = (row.fullName || `${row.firstName || ''} ${row.lastName || ''}`).trim().toLowerCase().replace(/\s+/g, ' ');
    return full;
};

const amountKey = (row: IWatrReimbursement): string => String(Math.round(Number(row.amount || 0) * 100));

const identityKeysForRow = (row: IWatrReimbursement): string[] => {
    const amount = amountKey(row);
    const keys: string[] = [];
    const email = normalizeEmail(row.email);
    const phone = normalizePhone(row.phone);
    const name = normalizeName(row);

    if (email) keys.push(`email:${email}|${amount}`);
    if (phone) keys.push(`phone:${phone}|${amount}`);
    if (name) keys.push(`name:${name}|${amount}`);
    return keys;
};

const identitiesMatch = (a: IWatrReimbursement, b: IWatrReimbursement): boolean => {
    const emailA = normalizeEmail(a.email);
    const emailB = normalizeEmail(b.email);
    const phoneA = normalizePhone(a.phone);
    const phoneB = normalizePhone(b.phone);
    const nameA = normalizeName(a);
    const nameB = normalizeName(b);

    const emailMatch = !!(emailA && emailB && emailA === emailB);
    const phoneMatch = !!(phoneA && phoneB && phoneA === phoneB);
    const nameMatch = !!(nameA && nameB && nameA === nameB);

    if (emailA && emailB) return emailMatch && amountKey(a) === amountKey(b);
    if (phoneA && phoneB) return phoneMatch && amountKey(a) === amountKey(b);
    return nameMatch && amountKey(a) === amountKey(b);
};

const isRiskyDuplicatePeer = (row: IWatrReimbursement, peer: IWatrReimbursement): boolean => {
    if (row.id === peer.id) return false;
    if (!identitiesMatch(row, peer)) return false;

    const sameRequest = row.requestId === peer.requestId;
    const confA = (row.confirmationCode || '').trim();
    const confB = (peer.confirmationCode || '').trim();
    const sameConfirmation = !!(confA && confB && confA === confB);

    if (!sameRequest) return true;
    if (sameConfirmation) return true;
    return false;
};

interface IProps {
    gridApi: GridApi,
    columnApi: ColumnApi,
    organTypes: OrganType[],
    onChangeOrganType: (organTypes: OrganType[]) => void,
    onSetGridComponents: (params: GridReadyEvent) => void,
    onUpdateSidebarView: (requestId: number) => void
}

interface IStateProps {
    app: IAppState
}

interface IDispatchProps {
    showLoading: (message: string) => Promise<void>,
    hideLoading: () => void,
    getAuditReport: (startDate: string, endDate: string, organType?: OrganType, query?: string) => Promise<IWatrAuditReportResponse>
}

type TProps = IProps & IStateProps & IDispatchProps;

interface IState {
    list: IWatrReimbursement[],
    startDate: string,
    endDate: string,
    fetching: boolean,
    findAlias: string,
    findName: string,
    findConfirmation: string,
    findCenter: string,
    findEmail: string,
    reimbursementType: number,
    paymentDirection: PaymentDirectionFilter,
    reimbursementFamily: ReimbursementFamilyFilter,
    preset: DatePreset,
    yearEndMode: boolean,
    discrepancy: DiscrepancyFilter,
    showAdvanced: boolean,
    duplicateResolutions: { [id: number]: 'not-duplicate' | 'confirmed' },
}

const emptyFlags = (): AuditExceptionFlags => ({
    missingConfirmation: false,
    missingApprover: false,
    complications: false,
    possibleDuplicate: false,
    highDollar: false,
    missingReimbursed: false,
    paidBeforeDonation: false,
    incompleteTaxId: false,
    incompleteSsn: false,
    incompleteAddress: false,
    manyPayments: false,
    typeMismatch: false,
});

const labelsFromFlags = (flags: AuditExceptionFlags): string => {
    const parts: string[] = [];
    if (flags.missingConfirmation) parts.push('No confirmation');
    if (flags.possibleDuplicate) parts.push('Possible duplicate');
    if (flags.highDollar) parts.push('High-dollar');
    if (flags.complications) parts.push('Complication');
    if (flags.paidBeforeDonation) parts.push('Pre-donation');
    if (flags.incompleteSsn && flags.incompleteAddress) parts.push('Incomplete SSN/address');
    else if (flags.incompleteSsn) parts.push('Incomplete SSN');
    else if (flags.incompleteAddress) parts.push('Incomplete address');
    if (flags.manyPayments) parts.push('Multiple payments');
    if (flags.typeMismatch) parts.push('Type mismatch');
    return parts.join(', ');
};

const mergeFlags = (rows: AuditDisplayRow[]): AuditExceptionFlags => {
    return rows.reduce((acc, row) => {
        const f = row.exceptionFlags || emptyFlags();
        return {
            missingConfirmation: acc.missingConfirmation || f.missingConfirmation,
            missingApprover: false,
            complications: acc.complications || f.complications,
            possibleDuplicate: acc.possibleDuplicate || f.possibleDuplicate,
            highDollar: acc.highDollar || f.highDollar,
            missingReimbursed: false,
            paidBeforeDonation: acc.paidBeforeDonation || f.paidBeforeDonation,
            incompleteTaxId: acc.incompleteTaxId || f.incompleteTaxId,
            incompleteSsn: acc.incompleteSsn || f.incompleteSsn,
            incompleteAddress: acc.incompleteAddress || f.incompleteAddress,
            manyPayments: acc.manyPayments || f.manyPayments,
            typeMismatch: acc.typeMismatch || f.typeMismatch,
        };
    }, emptyFlags());
};

const parseDonationDate = (organDonated: string): moment.Moment | null => {
    if (!(organDonated || '').trim()) return null;
    const m = moment(organDonated, ['MM/DD/YYYY', 'YYYY-MM-DD', moment.ISO_8601], true);
    return m.isValid() ? m : null;
};

const normalizeSsnDigits = (value?: string | null): string =>
    String(value == null ? '' : value).replace(/\D/g, '');

const hasValidSsn = (row: IWatrReimbursement): boolean => {
    const digits = normalizeSsnDigits(row.ssn);
    if (digits.length !== 9) return false;
    if (digits === '000000000') return false;
    return true;
};

const hasCompleteMailingAddress = (row: IWatrReimbursement): boolean => {
    return !!(row.address || '').trim()
        && !!(row.city || '').trim()
        && !!(row.state || '').trim()
        && !!(row.zip || '').trim();
};

const getTaxIdentityIssues = (row: IWatrReimbursement): { incompleteSsn: boolean, incompleteAddress: boolean } => ({
    incompleteSsn: !hasValidSsn(row),
    incompleteAddress: !hasCompleteMailingAddress(row),
});

const isPaidBeforeDonation = (row: IWatrReimbursement): boolean => {
    if (isAdvancedReimbursementType(row.reimbursementType)) return false;
    const donation = parseDonationDate(row.organDonated || '');
    if (!donation) return true;
    const paid = moment(row.date);
    if (!paid.isValid()) return false;
    return paid.isBefore(donation, 'day');
};

const isTypeMismatch = (row: IWatrReimbursement): boolean => {
    const weeks = (row.weeksRequested || '').trim();
    const travels = (row.travelsRequested || '').trim();
    if (WAGES_FAMILY.indexOf(row.reimbursementType) >= 0 && travels) return true;
    if (TRAVEL_FAMILY.indexOf(row.reimbursementType) >= 0 && weeks) return true;
    return false;
};

const annotateExceptions = (
    rows: IWatrReimbursement[],
    duplicateResolutions: { [id: number]: 'not-duplicate' | 'confirmed' }
): AuditDisplayRow[] => {
    const identityBuckets: { [key: string]: IWatrReimbursement[] } = {};

    rows.forEach(row => {
        identityKeysForRow(row).forEach(key => {
            if (!identityBuckets[key]) identityBuckets[key] = [];
            identityBuckets[key].push(row);
        });
    });

    return rows.map(row => {
        const candidateMap: { [id: number]: IWatrReimbursement } = {};
        identityKeysForRow(row).forEach(key => {
            (identityBuckets[key] || []).forEach(peer => {
                if (peer.id !== row.id) candidateMap[peer.id] = peer;
            });
        });

        const riskyPeers = Object.keys(candidateMap)
            .map(id => candidateMap[Number(id)])
            .filter(peer => isRiskyDuplicatePeer(row, peer))
            .map(toDuplicatePeer);

        const resolution = duplicateResolutions[row.id];
        const taxIssues = getTaxIdentityIssues(row);
        const requestPayments = rows
            .filter(x => x.requestId === row.requestId)
            .map(toDuplicatePeer)
            .sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
        const highDollarEval = evaluateHighDollarPayments(
            rows
                .filter(x => x.requestId === row.requestId)
                .map(x => ({
                    id: x.id,
                    amount: Number(x.amount || 0),
                    reimbursementType: x.reimbursementType,
                    date: x.date,
                }))
        );
        const flags: AuditExceptionFlags = {
            missingConfirmation: !(row.confirmationCode || '').trim(),
            missingApprover: false,
            complications: COMPLICATION_TYPES.indexOf(row.reimbursementType) >= 0,
            possibleDuplicate: riskyPeers.length > 0 && resolution !== 'not-duplicate',
            highDollar: highDollarEval.highDollarIds.indexOf(row.id) >= 0,
            missingReimbursed: false,
            paidBeforeDonation: isPaidBeforeDonation(row),
            incompleteSsn: taxIssues.incompleteSsn,
            incompleteAddress: taxIssues.incompleteAddress,
            incompleteTaxId: taxIssues.incompleteSsn || taxIssues.incompleteAddress,
            manyPayments: requestPayments.length >= MANY_PAYMENTS_THRESHOLD,
            typeMismatch: isTypeMismatch(row),
        };
        return {
            ...row,
            exceptionFlags: flags,
            exceptionLabels: labelsFromFlags(flags),
            duplicatePeers: riskyPeers,
            relatedPayments: requestPayments,
        };
    });
};

const rollupByRequest = (rows: AuditDisplayRow[]): AuditDisplayRow[] => {
    const groups = _.groupBy(rows, r => `${r.requestId}|${yearEndFamilyKey(r.reimbursementType)}`);
    return Object.keys(groups).map(key => {
        const items = groups[key];
        const first = items[0];
        const flags = mergeFlags(items);
        flags.manyPayments = items.length >= MANY_PAYMENTS_THRESHOLD || flags.manyPayments;
        const taxIssues = getTaxIdentityIssues(first);
        flags.incompleteSsn = taxIssues.incompleteSsn;
        flags.incompleteAddress = taxIssues.incompleteAddress;
        flags.incompleteTaxId = taxIssues.incompleteSsn || taxIssues.incompleteAddress;
        const latestDate = items
            .map(x => x.date)
            .filter(Boolean)
            .sort()
            .reverse()[0] || first.date;
        const weeksMerged = _.uniq(
            items
                .map(x => (x.weeksRequested || '').trim())
                .filter(Boolean)
                .join(',')
                .split(/[,;\s]+/)
                .map(x => x.trim())
                .filter(Boolean)
        ).sort((a, b) => Number(a) - Number(b) || String(a).localeCompare(String(b)));
        const travelsMerged = _.uniq(
            items
                .map(x => (x.travelsRequested || '').trim())
                .filter(Boolean)
                .join(',')
                .split(/[,;\s]+/)
                .map(x => x.trim())
                .filter(Boolean)
        ).sort((a, b) => Number(a) - Number(b) || String(a).localeCompare(String(b)));
        return {
            ...first,
            id: first.id,
            amount: _.sumBy(items, x => Number(x.amount || 0)),
            count: items.length,
            date: latestDate,
            weeksRequested: weeksMerged.join(', '),
            travelsRequested: travelsMerged.join(', '),
            exceptionFlags: flags,
            exceptionLabels: labelsFromFlags(flags),
            confirmationCode: '',
            approverName: '',
            reimbursedUserName: '',
            relatedPayments: items.map(toDuplicatePeer).sort((a, b) => String(b.date || '').localeCompare(String(a.date || ''))),
        } as AuditDisplayRow;
    }).sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
};

const datesForPreset = (preset: DatePreset): { startDate: string, endDate: string, yearEndMode: boolean } => {
    if (preset === 'thisMonth') {
        return {
            startDate: moment().startOf('month').format('YYYY-MM-DD'),
            endDate: moment().format('YYYY-MM-DD'),
            yearEndMode: false,
        };
    }
    if (preset === 'priorYear') {
        const year = moment().year() - 1;
        return {
            startDate: `${year}-01-01`,
            endDate: `${year}-12-31`,
            yearEndMode: true,
        };
    }
    return {
        startDate: moment().subtract(90, 'days').format('YYYY-MM-DD'),
        endDate: moment().format('YYYY-MM-DD'),
        yearEndMode: false,
    };
};

const matchesDiscrepancy = (row: AuditDisplayRow, discrepancy: DiscrepancyFilter): boolean => {
    const f = row.exceptionFlags || emptyFlags();
    switch (discrepancy) {
        case 'all': return true;
        case 'noConfirmation': return f.missingConfirmation;
        case 'possibleDuplicate': return f.possibleDuplicate;
        case 'highDollar': return f.highDollar;
        case 'complications': return f.complications;
        case 'paidBeforeDonation': return f.paidBeforeDonation;
        case 'incompleteTaxId': return f.incompleteTaxId;
        case 'manyPayments': return f.manyPayments;
        case 'typeMismatch': return f.typeMismatch;
        default: return true;
    }
};

const WATR_BRAND_BLUE = '#3B76B5';

const auditToolbarStyles = {
    panel: {
        marginBottom: 10,
        marginTop: 2,
        padding: '10px 12px',
        background: '#fafbfc',
        border: '1px solid #e5eaf0',
        borderRadius: 6,
        fontSize: 14,
        color: '#333',
    } as React.CSSProperties,
    label: {
        fontSize: 13,
        fontWeight: 600,
        color: '#333',
        lineHeight: 1.2,
        marginBottom: 4,
    } as React.CSSProperties,
    fieldRow: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        alignItems: 'flex-end',
        gap: 12,
    } as React.CSSProperties,
    metricRow: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        alignItems: 'stretch',
        gap: 6,
        marginTop: 8,
    } as React.CSSProperties,
    metricTotals: {
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        marginLeft: 'auto',
        paddingLeft: 4,
        flexShrink: 0,
    } as React.CSSProperties,
    summaryStat: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 1,
        minWidth: 64,
    },
    summaryLabel: {
        fontSize: 12,
        fontWeight: 600,
        color: '#666',
    },
    summaryValue: {
        fontSize: 15,
        fontWeight: 700,
        color: '#333',
        fontVariantNumeric: 'tabular-nums' as const,
    },
    ghostBtn: {
        border: '1px solid #ccc',
        background: '#fff',
        color: '#333',
        borderRadius: 4,
        padding: '5px 12px',
        fontSize: 14,
        fontWeight: 600,
        lineHeight: 1.2,
        cursor: 'pointer',
        height: 30,
    } as React.CSSProperties,
    yearEndBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '3px 8px',
        borderRadius: 4,
        background: '#fff7ed',
        border: '1px solid #fdba74',
        color: '#9a3412',
        fontSize: 12,
        fontWeight: 700,
        lineHeight: 1.2,
    } as React.CSSProperties,
    advancedBlock: {
        marginTop: 10,
        paddingTop: 10,
        borderTop: '1px solid #e5eaf0',
    } as React.CSSProperties,
    focusHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap' as const,
        marginTop: 10,
        marginBottom: 2,
    } as React.CSSProperties,
};

const periodPillStyle = (active: boolean): React.CSSProperties => ({
    border: active ? `1px solid ${WATR_BRAND_BLUE}` : '1px solid #ccc',
    borderRadius: 4,
    padding: '5px 12px',
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1.2,
    cursor: 'pointer',
    height: 30,
    background: active ? WATR_BRAND_BLUE : '#fff',
    color: active ? '#fff' : '#333',
});

const discrepancyTileStyle = (active: boolean, risk: boolean, zero: boolean): React.CSSProperties => {
    let background = '#fff';
    let border = '1px solid #ccc';
    let color = '#333';
    let countColor = '#333';

    if (active && risk) {
        background = '#fff7ed';
        border = '1px solid #fdba74';
        color = '#9a3412';
        countColor = '#c2410c';
    } else if (active) {
        background = WATR_BRAND_BLUE;
        border = `1px solid ${WATR_BRAND_BLUE}`;
        color = '#fff';
        countColor = '#fff';
    } else if (zero) {
        color = '#999';
        countColor = '#999';
    } else if (risk) {
        border = '1px solid #fed7aa';
        background = '#fffbeb';
        countColor = '#c2410c';
    }

    return {
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',
        minWidth: 102,
        padding: '7px 9px',
        borderRadius: 6,
        border,
        background,
        color,
        cursor: 'pointer',
        textAlign: 'left',
        boxShadow: active ? '0 1px 3px rgba(59, 118, 181, 0.25)' : 'none',
        ['--count-color' as any]: countColor,
    };
};

class YearEndReport extends React.Component<TProps, IState> {
    readonly state: IState = {
        list: [],
        startDate: moment().subtract(90, 'days').format('YYYY-MM-DD'),
        endDate: moment().format('YYYY-MM-DD'),
        fetching: false,
        findAlias: '',
        findName: '',
        findConfirmation: '',
        findCenter: '',
        findEmail: '',
        reimbursementType: 0,
        paymentDirection: 'both',
        reimbursementFamily: 'both',
        preset: 'last90',
        yearEndMode: false,
        discrepancy: 'all',
        showAdvanced: false,
        duplicateResolutions: {},
    }

    onGridSizeChanged = (e: GridSizeChangedEvent) => {
        let grid = document.getElementById('grid-wrapper');

        if (grid == null)
            return;

        let gridWidth = grid.offsetWidth;

        let columnsToShow = [];
        let columnsToHide = [];

        let totalColsWidth = 0;

        let allColumns = e.columnApi.getAllColumns();

        for (let i = 0; i < allColumns.length; i++) {
            let column = allColumns[i];

            totalColsWidth += column.getMinWidth();

            if (totalColsWidth > gridWidth) {
                columnsToHide.push(column.getColId);
            } else {
                columnsToShow.push(column.getColId);
            }
        }

        e.columnApi.setColumnsVisible(columnsToShow, true);
        e.columnApi.setColumnsVisible(columnsToHide, false);

        e.api.sizeColumnsToFit();
    }

    onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    }

    componentDidMount = () => {
        this.getData(true);
    }

    componentDidUpdate = (prevProps: TProps) => {
        if (!_.isEqual(prevProps.organTypes, this.props.organTypes)) {
            this.setState({ discrepancy: 'all' }, () => this.getData(true));
        }
    }

    buildServerQuery = (): string => {
        const parts = [
            this.state.findAlias,
            this.state.findName,
            this.state.findConfirmation,
            this.state.findCenter,
            this.state.findEmail,
        ].map(x => (x || '').trim()).filter(Boolean);
        return parts.join(' ');
    }

    applyFieldFilters = (rows: IWatrReimbursement[]): IWatrReimbursement[] => {
        let filtered = rows;
        const alias = (this.state.findAlias || '').trim().toLowerCase();
        const name = (this.state.findName || '').trim().toLowerCase();
        const confirmation = (this.state.findConfirmation || '').trim().toLowerCase();
        const center = (this.state.findCenter || '').trim().toLowerCase();
        const email = (this.state.findEmail || '').trim().toLowerCase();

        if (alias) {
            filtered = filtered.filter(x => (x.donorAlias || '').toLowerCase().indexOf(alias) >= 0);
        }
        if (name) {
            filtered = filtered.filter(x => {
                const payee = (x.fullName || `${x.firstName || ''} ${x.lastName || ''}`).toLowerCase();
                return (x.firstName || '').toLowerCase().indexOf(name) >= 0
                    || (x.lastName || '').toLowerCase().indexOf(name) >= 0
                    || payee.indexOf(name) >= 0;
            });
        }
        if (confirmation) {
            filtered = filtered.filter(x => (x.confirmationCode || '').toLowerCase().indexOf(confirmation) >= 0);
        }
        if (center) {
            filtered = filtered.filter(x => (x.centerAlias || '').toLowerCase().indexOf(center) >= 0);
        }
        if (email) {
            filtered = filtered.filter(x => (x.email || '').toLowerCase().indexOf(email) >= 0);
        }
        return filtered;
    }

    getAnnotatedBaseList = (): AuditDisplayRow[] => {
        let filtered: IWatrReimbursement[] = this.state.list || [];
        const yearEndMode = this.state.yearEndMode;
        const family = this.state.reimbursementFamily;

        if (yearEndMode) {
            filtered = filtered.filter(x => isYearEndEligibleType(x.reimbursementType));
        }

        if (family === 'wages') {
            filtered = filtered.filter(x => isWagesFamilyType(x.reimbursementType));
        } else if (family === 'travel') {
            filtered = filtered.filter(x => isTravelFamilyType(x.reimbursementType));
        } else if (!yearEndMode && this.state.reimbursementType > 0) {
            filtered = filtered.filter(x => x.reimbursementType == this.state.reimbursementType);
        }

        filtered = this.applyFieldFilters(filtered);
        let display = annotateExceptions(filtered, this.state.duplicateResolutions);

        if (this.state.paymentDirection === 'debit') {
            display = display.filter(row => !isCreditReturnPayment(row));
        } else if (this.state.paymentDirection === 'credit') {
            display = display.filter(row => isCreditReturnPayment(row));
        }

        if (yearEndMode) {
            display = rollupByRequest(display);
        }

        return display;
    }

    handlePaymentDirection = (paymentDirection: PaymentDirectionFilter) => {
        this.setState({ paymentDirection, discrepancy: 'all' });
    }

    handleReimbursementFamily = (reimbursementFamily: ReimbursementFamilyFilter) => {
        this.setState({
            reimbursementFamily,
            reimbursementType: reimbursementFamily === 'both' ? this.state.reimbursementType : 0,
            discrepancy: 'all',
        });
    }

    getDisplayList = (): AuditDisplayRow[] => {
        let list = this.getAnnotatedBaseList().filter(row => matchesDiscrepancy(row, this.state.discrepancy));
        if (this.state.discrepancy === 'highDollar') {
            list = list.slice().sort((a, b) => Number(b.amount || 0) - Number(a.amount || 0));
        }
        return list;
    }

    getDiscrepancyCounts = (): { [key in DiscrepancyFilter]: number } => {
        const base = this.getAnnotatedBaseList();
        const counts = {
            all: base.length,
            noConfirmation: 0,
            possibleDuplicate: 0,
            highDollar: 0,
            complications: 0,
            paidBeforeDonation: 0,
            incompleteTaxId: 0,
            manyPayments: 0,
            typeMismatch: 0,
        };
        base.forEach(row => {
            const f = row.exceptionFlags || emptyFlags();
            if (f.missingConfirmation) counts.noConfirmation++;
            if (f.possibleDuplicate) counts.possibleDuplicate++;
            if (f.highDollar) counts.highDollar++;
            if (f.complications) counts.complications++;
            if (f.paidBeforeDonation) counts.paidBeforeDonation++;
            if (f.incompleteTaxId) counts.incompleteTaxId++;
            if (f.manyPayments) counts.manyPayments++;
            if (f.typeMismatch) counts.typeMismatch++;
        });
        return counts;
    }

    getData = (message: boolean) => {
        let start = message ? this.props.showLoading('Getting registrations, one moment please...') : Promise.resolve(null);
        let organTypes = this.props.organTypes || [];
        let organType = organTypes.length == 1 ? organTypes[0] : undefined;
        const query = this.buildServerQuery();

        start.then(() => {
            this.setState({
                fetching: message == true ? false : true
            }, () => {
                this.props.getAuditReport(this.state.startDate, this.state.endDate, organType, query)
                    .then(resp => {
                        if (resp.data.success) {
                            this.setState({
                                list: resp.data.results || [],
                                fetching: false
                            }, () => {
                                if (this.props.gridApi != null) {
                                    this.props.gridApi.refreshCells();
                                }
                            });
                        } else {
                            this.setState({ fetching: false });
                        }

                        this.props.hideLoading();
                    })
                    .catch(() => {
                        this.setState({ fetching: false });
                        this.props.hideLoading();
                    });
            });
        });
    }

    handleInputChange = (e: React.FormEvent<HTMLInputElement & FormControl & HTMLSelectElement>) => {
        let name = e.currentTarget.name;
        let value = e.currentTarget.value;

        this.setState({
            [name]: name === 'reimbursementType' ? parseInt(value, 10) || 0 : value
        } as any);
    }

    handlePreset = (preset: DatePreset) => {
        const next = datesForPreset(preset);
        this.setState({
            preset,
            startDate: next.startDate,
            endDate: next.endDate,
            yearEndMode: next.yearEndMode,
            reimbursementType: next.yearEndMode ? 0 : this.state.reimbursementType,
            discrepancy: 'all',
        }, () => this.getData(true));
    }

    handleDiscrepancy = (discrepancy: DiscrepancyFilter) => {
        if (discrepancy !== 'all' && this.state.discrepancy === discrepancy) {
            this.setState({ discrepancy: 'all' });
            return;
        }
        this.setState({ discrepancy });
    }

    getDiscrepancyFocusLabel = (discrepancy: DiscrepancyFilter): string => {
        switch (discrepancy) {
            case 'all': return 'All payments';
            case 'noConfirmation': return 'No confirmation #';
            case 'possibleDuplicate': return 'Possible duplicate';
            case 'highDollar': return `High-dollar ≥$${HIGH_DOLLAR_THRESHOLD.toLocaleString()}`;
            case 'complications': return 'Complications';
            case 'paidBeforeDonation': return 'Pre-donation / unconfirmed';
            case 'incompleteTaxId': return 'Incomplete SSN/address';
            case 'manyPayments': return 'Many payments';
            case 'typeMismatch': return 'Type mismatch';
            default: return 'All payments';
        }
    }

    getPeriodLabel = (): string => {
        if (this.state.preset === 'last90') return 'Last 90 days';
        if (this.state.preset === 'thisMonth') return 'This month';
        if (this.state.preset === 'priorYear') return 'Prior year';
        return `${moment(this.state.startDate).format('MM/DD/YYYY')} – ${moment(this.state.endDate).format('MM/DD/YYYY')}`;
    }

    getOrganLabel = (): string => {
        if (this.props.organTypes.length === 2) return 'All organs';
        if (this.props.organTypes[0] === OrganType.Liver) return 'Liver';
        return 'Kidney';
    }

    getPaymentLabel = (): string => {
        if (this.state.paymentDirection === 'debit') return 'Debit';
        if (this.state.paymentDirection === 'credit') return 'Credit';
        return 'Both';
    }

    getFamilyLabel = (): string => {
        if (this.state.reimbursementFamily === 'wages') return 'Wages';
        if (this.state.reimbursementFamily === 'travel') return 'Expenses';
        return 'Both';
    }

    handleToggleAdvanced = () => {
        this.setState({ showAdvanced: !this.state.showAdvanced });
    }

    handleResolveDuplicate = (reimbursementId: number, resolution: 'not-duplicate' | 'confirmed') => {
        this.setState({
            duplicateResolutions: {
                ...this.state.duplicateResolutions,
                [reimbursementId]: resolution,
            }
        });
    }

    handleSearch = () => {
        this.getData(true);
    }

    handleResetAdvanced = () => {
        this.setState({
            findAlias: '',
            findName: '',
            findConfirmation: '',
            findCenter: '',
            findEmail: '',
            reimbursementType: 0,
        }, () => this.getData(true));
    }

    renderDiscrepancyTile = (id: DiscrepancyFilter, label: string, count: number, risk?: boolean) => {
        const active = this.state.discrepancy === id;
        const style = discrepancyTileStyle(active, !!risk, count === 0 && id !== 'all');
        const countColor = (style as any)['--count-color'] || '#333';
        return (
            <button
                key={id}
                type='button'
                style={style}
                onClick={() => this.handleDiscrepancy(id)}
            >
                <span style={{ fontSize: 12, fontWeight: 600, lineHeight: 1.25, opacity: active && !risk ? 0.92 : 1 }}>{label}</span>
                <span style={{ marginTop: 3, fontSize: 16, fontWeight: 700, lineHeight: 1, color: countColor, fontVariantNumeric: 'tabular-nums' }}>
                    {count.toLocaleString()}
                </span>
            </button>
        );
    }

    render = () => {
        const displayList = this.getDisplayList();
        const totalAmount = displayList.reduce((sum, row) => sum + Number(row.amount || 0), 0);
        const yearEndMode = this.state.yearEndMode;
        const counts = this.getDiscrepancyCounts();

        return (
            <>
                <div style={{ display: this.state.fetching ? 'flex' : 'none', justifyContent: 'space-between', flexWrap: 'wrap', marginBottom: 20 }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ display: 'flex' }}>
                            {this.state.fetching && (
                                <>
                                    <div style={{ marginRight: 5 }}>Fetching Watr Requests...</div>
                                    <Loader
                                        size={20}
                                        color={'#a1a1a1'}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <MediaQuery minWidth={1023} style={{ padding: 0 }}>
                    <div style={auditToolbarStyles.panel}>
                        <div style={auditToolbarStyles.fieldRow}>
                            <div>
                                <div style={auditToolbarStyles.label}>Period</div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
                                    <button type='button' style={periodPillStyle(this.state.preset === 'last90')} onClick={() => this.handlePreset('last90')}>
                                        Last 90 days
                                    </button>
                                    <button type='button' style={periodPillStyle(this.state.preset === 'thisMonth')} onClick={() => this.handlePreset('thisMonth')}>
                                        This month
                                    </button>
                                    <button type='button' style={periodPillStyle(this.state.preset === 'priorYear')} onClick={() => this.handlePreset('priorYear')}>
                                        Prior year
                                    </button>
                                </div>
                            </div>
                            <div>
                                <div style={auditToolbarStyles.label}>Organ</div>
                                <ToggleButtonGroup
                                    type='radio'
                                    name='organs'
                                    value={this.props.organTypes.length == 2 ? -1 : this.props.organTypes[0]}
                                >
                                    <ToggleButton
                                        value={-1}
                                        onChange={() => this.props.onChangeOrganType([OrganType.Kidney, OrganType.Liver])}
                                        className={this.props.organTypes.length == 2 ? 'btn-primary btn-sm' : 'btn-sm'}
                                    >
                                        All
                                    </ToggleButton>
                                    <ToggleButton
                                        value={OrganType.Kidney}
                                        onChange={() => this.props.onChangeOrganType([OrganType.Kidney])}
                                        className={this.props.organTypes.length == 1 && this.props.organTypes.includes(OrganType.Kidney) ? 'btn-primary btn-sm' : 'btn-sm'}
                                    >
                                        Kidney
                                    </ToggleButton>
                                    <ToggleButton
                                        value={OrganType.Liver}
                                        onChange={() => this.props.onChangeOrganType([OrganType.Liver])}
                                        className={this.props.organTypes.length == 1 && this.props.organTypes.includes(OrganType.Liver) ? 'btn-primary btn-sm' : 'btn-sm'}
                                    >
                                        Liver
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </div>
                            <div>
                                <div style={auditToolbarStyles.label}>Payment</div>
                                <ToggleButtonGroup
                                    type='radio'
                                    name='paymentDirection'
                                    value={this.state.paymentDirection}
                                >
                                    <ToggleButton
                                        value='both'
                                        onChange={() => this.handlePaymentDirection('both')}
                                        className={this.state.paymentDirection === 'both' ? 'btn-primary btn-sm' : 'btn-sm'}
                                    >
                                        Both
                                    </ToggleButton>
                                    <ToggleButton
                                        value='debit'
                                        onChange={() => this.handlePaymentDirection('debit')}
                                        className={this.state.paymentDirection === 'debit' ? 'btn-primary btn-sm' : 'btn-sm'}
                                    >
                                        Debit
                                    </ToggleButton>
                                    <ToggleButton
                                        value='credit'
                                        onChange={() => this.handlePaymentDirection('credit')}
                                        className={this.state.paymentDirection === 'credit' ? 'btn-primary btn-sm' : 'btn-sm'}
                                    >
                                        Credit
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </div>
                            <div>
                                <div style={auditToolbarStyles.label}>Reimbursement Type</div>
                                <ToggleButtonGroup
                                    type='radio'
                                    name='reimbursementFamily'
                                    value={this.state.reimbursementFamily}
                                >
                                    <ToggleButton
                                        value='both'
                                        onChange={() => this.handleReimbursementFamily('both')}
                                        className={this.state.reimbursementFamily === 'both' ? 'btn-primary btn-sm' : 'btn-sm'}
                                    >
                                        Both
                                    </ToggleButton>
                                    <ToggleButton
                                        value='wages'
                                        onChange={() => this.handleReimbursementFamily('wages')}
                                        className={this.state.reimbursementFamily === 'wages' ? 'btn-primary btn-sm' : 'btn-sm'}
                                    >
                                        Wages
                                    </ToggleButton>
                                    <ToggleButton
                                        value='travel'
                                        onChange={() => this.handleReimbursementFamily('travel')}
                                        className={this.state.reimbursementFamily === 'travel' ? 'btn-primary btn-sm' : 'btn-sm'}
                                    >
                                        Expenses
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </div>
                            <div style={{ marginLeft: 'auto', paddingBottom: 0 }}>
                                <button type='button' style={auditToolbarStyles.ghostBtn} onClick={this.handleToggleAdvanced}>
                                    {this.state.showAdvanced ? 'Hide advanced' : 'Advanced search'}
                                </button>
                            </div>
                        </div>

                        <div style={auditToolbarStyles.focusHeader}>
                            <div style={{ ...auditToolbarStyles.label, marginBottom: 0 }}>Discrepancy focus</div>
                            {yearEndMode && (
                                <span style={auditToolbarStyles.yearEndBadge}>
                                    Year End · {this.getFamilyLabel().toLowerCase()} rollup
                                </span>
                            )}
                        </div>
                        <div style={auditToolbarStyles.metricRow}>
                            {this.renderDiscrepancyTile('all', 'All payments', counts.all)}
                            {this.renderDiscrepancyTile('noConfirmation', 'No confirmation #', counts.noConfirmation, true)}
                            {this.renderDiscrepancyTile('possibleDuplicate', 'Possible duplicate', counts.possibleDuplicate, true)}
                            {this.renderDiscrepancyTile('highDollar', `High-dollar ≥$${HIGH_DOLLAR_THRESHOLD.toLocaleString()}`, counts.highDollar, true)}
                            {this.renderDiscrepancyTile('complications', 'Complications', counts.complications, true)}
                            {this.renderDiscrepancyTile('paidBeforeDonation', 'Pre-donation / unconfirmed', counts.paidBeforeDonation, true)}
                            {yearEndMode && this.renderDiscrepancyTile('incompleteTaxId', 'Incomplete SSN/address', counts.incompleteTaxId, true)}
                            {this.renderDiscrepancyTile('manyPayments', 'Many payments', counts.manyPayments, true)}
                            {this.renderDiscrepancyTile('typeMismatch', 'Type mismatch', counts.typeMismatch, true)}
                            <div style={auditToolbarStyles.metricTotals}>
                                <div style={auditToolbarStyles.summaryStat}>
                                    <span style={auditToolbarStyles.summaryLabel}>Rows</span>
                                    <span style={auditToolbarStyles.summaryValue}>{displayList.length.toLocaleString()}</span>
                                </div>
                                <div style={auditToolbarStyles.summaryStat}>
                                    <span style={auditToolbarStyles.summaryLabel}>Total</span>
                                    <span style={auditToolbarStyles.summaryValue}>
                                        ${totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {this.state.showAdvanced && (
                            <div style={{ ...auditToolbarStyles.advancedBlock, ...auditToolbarStyles.fieldRow }}>
                                <FormGroup style={{ marginBottom: 0, minWidth: 140 }}>
                                    <label style={auditToolbarStyles.label}>Start</label>
                                    <FormControl
                                        type='date'
                                        name='startDate'
                                        value={this.state.startDate}
                                        onChange={this.handleInputChange}
                                        bsSize='sm'
                                    />
                                </FormGroup>
                                <FormGroup style={{ marginBottom: 0, minWidth: 140 }}>
                                    <label style={auditToolbarStyles.label}>End</label>
                                    <FormControl
                                        type='date'
                                        name='endDate'
                                        value={this.state.endDate}
                                        onChange={this.handleInputChange}
                                        bsSize='sm'
                                    />
                                </FormGroup>
                                {!yearEndMode && this.state.reimbursementFamily === 'both' && (
                                    <FormGroup style={{ marginBottom: 0, minWidth: 160 }}>
                                        <label style={auditToolbarStyles.label}>Detail type</label>
                                        <FormControl
                                            componentClass='select'
                                            name='reimbursementType'
                                            value={this.state.reimbursementType}
                                            onChange={this.handleInputChange}
                                            bsSize='sm'
                                        >
                                            <option value={0}>All</option>
                                            <option value={ReimbursementType.Wages}>Wages</option>
                                            <option value={ReimbursementType.TravelAndLodging}>Travel</option>
                                            <option value={ReimbursementType.WagesForComplication}>Wages (Complication)</option>
                                            <option value={ReimbursementType.TravelAndLodgingForComplication}>Travel (Complication)</option>
                                            <option value={ReimbursementType.AdvancedReimbursement}>Adv Reimbursement</option>
                                            <option value={ReimbursementType.InternationalWorkup}>International Workup</option>
                                            <option value={ReimbursementType.DependentCare}>Dependent Care</option>
                                            <option value={ReimbursementType.DppReimbursement}>DPP</option>
                                        </FormControl>
                                    </FormGroup>
                                )}
                                <FormGroup style={{ marginBottom: 0, minWidth: 120 }}>
                                    <label style={auditToolbarStyles.label}>Alias</label>
                                    <FormControl type='text' name='findAlias' value={this.state.findAlias} onChange={this.handleInputChange} bsSize='sm' />
                                </FormGroup>
                                <FormGroup style={{ marginBottom: 0, minWidth: 120 }}>
                                    <label style={auditToolbarStyles.label}>Name</label>
                                    <FormControl type='text' name='findName' value={this.state.findName} onChange={this.handleInputChange} bsSize='sm' />
                                </FormGroup>
                                <FormGroup style={{ marginBottom: 0, minWidth: 120 }}>
                                    <label style={auditToolbarStyles.label}>Confirmation #</label>
                                    <FormControl type='text' name='findConfirmation' value={this.state.findConfirmation} onChange={this.handleInputChange} bsSize='sm' />
                                </FormGroup>
                                <FormGroup style={{ marginBottom: 0, minWidth: 110 }}>
                                    <label style={auditToolbarStyles.label}>Center</label>
                                    <FormControl type='text' name='findCenter' value={this.state.findCenter} onChange={this.handleInputChange} bsSize='sm' />
                                </FormGroup>
                                <FormGroup style={{ marginBottom: 0, minWidth: 140 }}>
                                    <label style={auditToolbarStyles.label}>Email</label>
                                    <FormControl type='text' name='findEmail' value={this.state.findEmail} onChange={this.handleInputChange} bsSize='sm' />
                                </FormGroup>
                                <Button bsStyle='primary' bsSize='small' onClick={this.handleSearch}>
                                    Search
                                </Button>
                                <Button bsSize='small' onClick={this.handleResetAdvanced}>
                                    Clear
                                </Button>
                            </div>
                        )}
                    </div>

                    {!this.state.fetching && displayList.length === 0 && (
                        <div style={{ margin: '8px 0 10px', fontSize: 13, color: '#64748b' }}>
                            No payments match the current filters
                            {this.state.discrepancy !== 'all' && (
                                <>
                                    {' · '}
                                    <a href='#' onClick={(e) => { e.preventDefault(); this.handleDiscrepancy('all'); }}>
                                        Clear focus
                                    </a>
                                </>
                            )}
                        </div>
                    )}

                    <YearEndReportGrid
                        data={displayList}
                        yearEndMode={yearEndMode}
                        duplicateResolutions={this.state.duplicateResolutions}
                        onGridReady={this.props.onSetGridComponents}
                        onFirstDataRendered={this.onFirstDataRendered}
                        onGridSizeChanged={this.onGridSizeChanged}
                        onUpdateSidebarView={this.props.onUpdateSidebarView}
                        onResolveDuplicate={this.handleResolveDuplicate}
                    />
                </MediaQuery>
            </>
        );
    }
}

const mapStateToProps = (state: IApplicationState): IStateProps => {
    return {
        app: state.app
    };
}

const mapDispatchToProps = (dispatch: Dispatch<any>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            return dispatch(AppActionCreators.hideLoading());
        },
        getAuditReport: (startDate, endDate, organType, query) => {
            return dispatch(WatrActionCreators.getAuditReport(startDate, endDate, organType, query));
        }
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(YearEndReport);
